import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  "Shoes": "ri-footprint-line",
  "Hoodies/Sweaters": "ri-t-shirt-line",
  "T-Shirts": "ri-shirt-line",
  "Jackets": "ri-cloud-line",
  "Pants/Shorts": "ri-pantone-line",
  "Headwear": "ri-user-smile-line",
  "Sets": "ri-stack-line",
  "Underwear/Underpants": "ri-contrast-drop-line",
  "Jersey": "ri-basketball-line",
  "Accessories": "ri-vip-crown-line",
  "Bags": "ri-briefcase-4-line",
  "Belts": "ri-link",
  "Perfumes": "ri-flask-line",
  "Other Stuff": "ri-more-2-line",
};

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap">
              Kakobuy <span className="text-accent-500">Finds</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap"
            >
              {t('nav_home')}
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
                        <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-xs`}></i>
                      </span>
                      <span>
                        <span className="font-medium">{cat.name}</span>
                        <span className="text-foreground-400 ml-2 text-xs">{cat.namePl}</span>
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/artykul-kakobuy-poradnik"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap"
            >
              {t('nav_articles')}
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('btn_kakobuy_sheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('btn_buy_now')} <i className="ri-arrow-right-line ml-1"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Enhanced */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-5 max-h-[70vh] overflow-y-auto">
            {/* Quick Nav */}
            <div className="flex items-center gap-2 mb-5">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center px-4 py-2 text-sm font-semibold text-foreground-700 bg-background-100 rounded-lg hover:bg-background-200 transition-colors"
              >
                <i className="ri-home-4-line mr-1.5"></i>{t('nav_home')}
              </Link>
              <Link
                to="/artykul-kakobuy-poradnik"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center px-4 py-2 text-sm font-semibold text-foreground-700 bg-background-100 rounded-lg hover:bg-background-200 transition-colors"
              >
                <i className="ri-article-line mr-1.5"></i>{t('nav_articles')}
              </Link>
            </div>

            {/* Category Section Header */}
            <div className="flex items-center gap-2 mb-3 px-1">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="text-xs font-semibold text-foreground-500 tracking-widest uppercase">{t('nav_categories')}</span>
              <span className="text-xs text-foreground-400 ml-auto">{categories.length} kategorii</span>
            </div>

            {/* Category Grid */}
            <div className="grid grid-cols-2 gap-1.5 mb-5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors group"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-400 group-hover:bg-accent-100 group-hover:text-accent-600 transition-colors shrink-0">
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-xs`}></i>
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-xs truncate">{cat.name}</div>
                    <div className="text-[10px] text-foreground-400 truncate">{cat.namePl}</div>
                  </div>
                </a>
              ))}
            </div>

            {/* Article Links */}
            <div className="border-t border-background-200/60 pt-4 mb-4">
              <span className="text-xs font-semibold text-foreground-500 tracking-widest uppercase px-1 mb-3 block">
                Porównania
              </span>
              <div className="space-y-1">
                <Link to="/artykul-kakobuy-poradnik" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <i className="ri-file-text-line text-foreground-400"></i>Kakobuy Spreadsheet – Poradnik
                </Link>
                <Link to="/cssbuy-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <i className="ri-file-text-line text-foreground-400"></i>CSSBuy Spreadsheet
                </Link>
                <Link to="/superbuy-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <i className="ri-file-text-line text-foreground-400"></i>Superbuy Spreadsheet
                </Link>
                <Link to="/oopbuy-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors">
                  <i className="ri-file-text-line text-foreground-400"></i>Oopbuy Spreadsheet
                </Link>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2 pt-3 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg text-center hover:bg-primary-600 transition-colors"
              >
                {t('btn_kakobuy_sheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-semibold bg-accent-500 text-background-50 rounded-lg text-center hover:bg-accent-600 transition-colors"
              >
                {t('btn_buy_now')} <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}